import { useCallback, useContext, useEffect, useRef, useState } from "react";
import KycVaraService from "../../../api/services/KycVaraService";
import { ProfileContext } from "../../../context/ProfileProvider";
import { alertErrorMessage } from "../../../customComponents/CustomAlertMessage";
import { openDiditVerification } from "./openDiditVerification";
import { QUESTIONS } from "./content";

/** Statuses that are shown on the status screen instead of the form. */
const STATUS_SCREEN_STATUSES = ["VERIFICATION_STARTED", "IN_REVIEW", "APPROVED", "REJECTED", "EXPIRED"];
/** Statuses that will not change any more (no "Refresh status" link). */
const FINAL_STATUSES = ["APPROVED", "REJECTED", "EXPIRED"];

/** Order of the screens, used to decide whether the carousel slides forward or back. */
const SCREEN_ORDER = ["start", "accountType", "income", "assets", "businessType", "result", "status"];

const EMPTY_ANSWERS = { accountType: null, income: null, assets: null, businessType: null };

function answersFrom(application) {
  if (!application) return EMPTY_ANSWERS;
  return {
    accountType: application.account_type,
    income: application.answers.income_gte_700k,
    assets: application.answers.assets_gte_3_5m,
    businessType: application.answers.business_type,
  };
}

/** The numbered form steps for the path the user is on. Business type only for companies with Yes / Yes. */
export function formStepsFor(answers) {
  const steps = ["accountType", "income", "assets"];
  const needsBusinessType = answers.accountType === "CORPORATE" && answers.income === true && answers.assets === true;
  if (needsBusinessType) steps.push("businessType");
  steps.push("result");
  return steps;
}

/** Which screen to show for an application loaded from the server (resume after reload or login). */
function screenFor(application) {
  if (!application) return "start";
  if (STATUS_SCREEN_STATUSES.includes(application.status)) return "status";
  if (application.status === "CLASSIFIED") return "result";

  const { answers } = application;
  if (answers.income_gte_700k === null) return "income";
  if (answers.assets_gte_3_5m === null) return "assets";
  // All answered but not classified yet: reopen the last question so Continue classifies.
  return application.needs_business_type ? "businessType" : "assets";
}

function isAlreadyVerified(application, userDetails) {
  // An approval closed for re-KYC (document expired / admin) means the user must verify again.
  if (application?.status === "APPROVED") return !application.rekyc_required;
  return !application && Number(userDetails?.kycVerified) === 2;
}

/**
 * All state and server calls for the KYC VARA form.
 * `onAlreadyVerified` runs when the user has nothing to do (approved or legacy verified).
 * With `closeWhenVerified: false` (opened from a button) an approved application shows its status instead.
 */
export function useKycVaraFlow({ userDetails, onAlreadyVerified, closeWhenVerified = true }) {
  const { handleUserDetails } = useContext(ProfileContext) || {};

  const [screen, setScreen] = useState("loading");
  const [direction, setDirection] = useState("forward");
  const [application, setApplication] = useState(null);
  const [answers, setAnswers] = useState(EMPTY_ANSWERS);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  /** Set when the last approval was closed (document expired / admin): start screen explains why. */
  const [reKycReason, setReKycReason] = useState(null);

  // Latest values for callbacks, without re-creating the callbacks on every change.
  const userDetailsRef = useRef(userDetails);
  userDetailsRef.current = userDetails;
  const statusRef = useRef(null);
  statusRef.current = application?.status || null;
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const onAlreadyVerifiedRef = useRef(onAlreadyVerified);
  onAlreadyVerifiedRef.current = onAlreadyVerified;
  const closeWhenVerifiedRef = useRef(closeWhenVerified);
  closeWhenVerifiedRef.current = closeWhenVerified;

  /** Change screen and remember which way the carousel should slide. */
  const goTo = useCallback((next) => {
    const from = SCREEN_ORDER.indexOf(screenRef.current);
    const to = SCREEN_ORDER.indexOf(next);
    setDirection(from !== -1 && to !== -1 && to < from ? "back" : "forward");
    setScreen(next);
  }, []);

  const showApplication = useCallback((next) => {
    setApplication(next);
    setAnswers(answersFrom(next));
  }, []);

  const loadApplication = useCallback(async () => {
    setScreen("loading");
    const result = await KycVaraService.getCurrentApplication();
    if (!result?.success) {
      setScreen("error");
      return;
    }
    const current = result.data || null;
    if (isAlreadyVerified(current, userDetailsRef.current)) {
      // Show the "verified" status only when the user asked for it and there is an application to show.
      const showStatus = current && !closeWhenVerifiedRef.current;
      if (!showStatus) {
        onAlreadyVerifiedRef.current?.();
        return;
      }
    }
    setDirection("forward");

    if (current?.rekyc_required) {
      // The closed approval stays as history on the server; the user starts a fresh application.
      setApplication(null);
      setAnswers({ ...EMPTY_ANSWERS, accountType: current.account_type });
      setReKycReason(current.closed_reason || "DOCUMENT_EXPIRED");
      setScreen("start");
      return;
    }

    showApplication(current);
    setScreen(screenFor(current));
  }, [showApplication]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  /**
   * One status call — no polling. Used once when Didit's window closes and when the user
   * presses "Refresh status". Opening the modal / reloading the page loads it via loadApplication.
   */
  const refreshApplication = useCallback(async () => {
    setRefreshing(true);
    try {
      const result = await KycVaraService.getCurrentApplication();
      if (!result?.success || !result.data) return;
      // A new status can change kycVerified (header badge, locked features): reload the profile too.
      if (result.data.status !== statusRef.current) handleUserDetails?.();
      setApplication(result.data);
    } finally {
      setRefreshing(false);
    }
  }, [handleUserDetails]);

  const isWaitingForDecision =
    screen === "status" && Boolean(application) && !FINAL_STATUSES.includes(application.status);

  /** Known backend error codes move the user to the right place; anything else is a toast. */
  const handleRequestError = useCallback(
    (result) => {
      switch (result?.code) {
        case "KYC_ALREADY_APPROVED":
          onAlreadyVerifiedRef.current?.();
          break;
        case "ACTIVE_APPLICATION_EXISTS":
        case "APPLICATION_LOCKED":
        case "VERIFICATION_ALREADY_SUBMITTED":
          loadApplication();
          break;
        case "RECLASSIFY_REQUIRED":
          alertErrorMessage(result.message);
          goTo("assets");
          break;
        default:
          alertErrorMessage(result?.message || "Something went wrong. Please try again.");
      }
    },
    [goTo, loadApplication]
  );

  /** Runs one API call with the busy state; returns `data`, or null after handling the error. */
  const runRequest = useCallback(
    async (request) => {
      setBusy(true);
      try {
        const result = await request();
        if (!result?.success) {
          handleRequestError(result);
          return null;
        }
        return result.data;
      } catch {
        alertErrorMessage("Network error. Please check your connection and try again.");
        return null;
      } finally {
        setBusy(false);
      }
    },
    [handleRequestError]
  );

  const setAnswer = (field) => (value) => setAnswers((current) => ({ ...current, [field]: value }));

  // ---------- step actions ----------

  const continueFromAccountType = async () => {
    let saved = application;
    if (!application) {
      saved = await runRequest(() => KycVaraService.createApplication(answers.accountType));
    } else if (application.account_type !== answers.accountType) {
      // Switching account type clears the earlier answers on the server.
      saved = await runRequest(() => KycVaraService.saveAnswers(application.id, { account_type: answers.accountType }));
    }
    if (!saved) return;
    showApplication(saved);
    goTo("income");
  };

  const continueFromIncome = async () => {
    const saved = await runRequest(() =>
      KycVaraService.saveAnswers(application.id, { [QUESTIONS.income.field]: answers.income })
    );
    if (!saved) return;
    showApplication(saved);
    goTo("assets");
  };

  const classifyAndShowResult = async (applicationId) => {
    const classified = await runRequest(() => KycVaraService.classify(applicationId));
    if (!classified) return;
    showApplication(classified);
    goTo("result");
  };

  const continueFromAssets = async () => {
    const saved = await runRequest(() =>
      KycVaraService.saveAnswers(application.id, { [QUESTIONS.assets.field]: answers.assets })
    );
    if (!saved) return;
    showApplication(saved);
    if (saved.needs_business_type) {
      goTo("businessType");
    } else {
      await classifyAndShowResult(saved.id);
    }
  };

  const continueFromBusinessType = async () => {
    const saved = await runRequest(() =>
      KycVaraService.saveAnswers(application.id, { business_type: answers.businessType })
    );
    if (!saved) return;
    showApplication(saved);
    await classifyAndShowResult(saved.id);
  };

  /** Used for the first start and for "Continue verification" (the server returns the same session). */
  const startVerification = async () => {
    const started = await runRequest(() => KycVaraService.startVerification(application.id));
    if (!started) return;
    showApplication(started);
    goTo("status");
    if (started.verification_url) {
      await openDiditVerification(started.verification_url, { onFinished: refreshApplication });
    }
  };

  /** After REJECTED / EXPIRED: a new application, keeping the account type as the default choice. */
  const restart = () => {
    const previousAccountType = application?.account_type || null;
    setApplication(null);
    setAnswers({ ...EMPTY_ANSWERS, accountType: previousAccountType });
    goTo("accountType");
  };

  const formSteps = formStepsFor(answers);
  const stepNumber = formSteps.indexOf(screen) + 1;
  const goBack = () => goTo(stepNumber > 1 ? formSteps[stepNumber - 2] : "start");

  return {
    screen,
    direction,
    reKycReason,
    application,
    answers,
    busy,
    refreshing,
    isWaitingForDecision,
    refreshStatus: refreshApplication,
    stepNumber,
    totalSteps: formSteps.length,
    setAnswer,
    goTo,
    goBack,
    reload: loadApplication,
    actions: {
      continueFromAccountType,
      continueFromIncome,
      continueFromAssets,
      continueFromBusinessType,
      startVerification,
      restart,
    },
  };
}
